import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  invalidateAll,
  invalidateList,
  pluralNotificationTitle,
  useNotificationContext,
} from '@/hooks/use-notification-mutation-helpers';

const NOT_AUTHENTICATED_ERROR = 'Not authenticated';
const ERROR_TOAST_TITLE_KEY = 'toasts.errorTitle';

export function useMarkNotificationAsRead() {
  const { queryClient, user, currentOrganizationId } = useNotificationContext();

  return useMutation({
    mutationKey: ['notifications'],
    mutationFn: async (notificationId: string) => {
      if (!user?.id || !currentOrganizationId) {
        throw new Error(NOT_AUTHENTICATED_ERROR);
      }

      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('recipient_user_id', user.id)
        .eq('organization_id', currentOrganizationId);

      if (error) {
        console.error('[useMarkNotificationAsRead] Error:', error);
        throw error;
      }
    },
    onSuccess: () => invalidateAll(queryClient, currentOrganizationId, user?.id),
  });
}

export function useMarkNotificationAsUnread() {
  const { queryClient, user, currentOrganizationId } = useNotificationContext();

  return useMutation({
    mutationKey: ['notifications'],
    mutationFn: async (notificationId: string) => {
      if (!user?.id || !currentOrganizationId) {
        throw new Error(NOT_AUTHENTICATED_ERROR);
      }

      const { error } = await supabase
        .from('notifications')
        .update({ read_at: null, dismissed_at: null })
        .eq('id', notificationId)
        .eq('recipient_user_id', user.id)
        .eq('organization_id', currentOrganizationId);

      if (error) {
        console.error('[useMarkNotificationAsUnread] Error:', error);
        throw error;
      }
    },
    onSuccess: () => invalidateAll(queryClient, currentOrganizationId, user?.id),
  });
}

export function useMarkAllNotificationsAsRead() {
  const { queryClient, user, currentOrganizationId } = useNotificationContext();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationKey: ['notifications'],
    mutationFn: async () => {
      if (!user?.id || !currentOrganizationId) {
        throw new Error('User or organization not found');
      }

      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('recipient_user_id', user.id)
        .eq('organization_id', currentOrganizationId)
        .is('read_at', null);

      if (error) {
        console.error('[useMarkAllNotificationsAsRead] Error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      invalidateAll(queryClient, currentOrganizationId, user?.id);
      toast({ title: t('toasts.allMarkedRead') });
    },
    onError: (error: Error) => {
      toast({
        title: t(ERROR_TOAST_TITLE_KEY),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDismissNotification() {
  const { queryClient, user, currentOrganizationId } = useNotificationContext();

  return useMutation({
    mutationKey: ['notifications'],
    mutationFn: async (notificationId: string) => {
      if (!user?.id || !currentOrganizationId) {
        throw new Error(NOT_AUTHENTICATED_ERROR);
      }

      const { error } = await supabase
        .from('notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('recipient_user_id', user.id)
        .eq('organization_id', currentOrganizationId);

      if (error) {
        console.error('[useDismissNotification] Error:', error);
        throw error;
      }
    },
    onSuccess: () => invalidateAll(queryClient, currentOrganizationId, user?.id),
  });
}

export function useSaveNotification() {
  const { queryClient, user, currentOrganizationId } = useNotificationContext();

  return useMutation({
    mutationKey: ['notifications'],
    mutationFn: async (notificationId: string) => {
      if (!user?.id || !currentOrganizationId) {
        throw new Error(NOT_AUTHENTICATED_ERROR);
      }

      const { error } = await supabase
        .from('notifications')
        .update({ saved_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('recipient_user_id', user.id)
        .eq('organization_id', currentOrganizationId);

      if (error) {
        console.error('[useSaveNotification] Error:', error);
        throw error;
      }
    },
    onSuccess: () => invalidateList(queryClient, currentOrganizationId, user?.id),
  });
}

export function useUnsaveNotification() {
  const { queryClient, user, currentOrganizationId } = useNotificationContext();

  return useMutation({
    mutationKey: ['notifications'],
    mutationFn: async (notificationId: string) => {
      if (!user?.id || !currentOrganizationId) {
        throw new Error(NOT_AUTHENTICATED_ERROR);
      }

      const { error } = await supabase
        .from('notifications')
        .update({ saved_at: null })
        .eq('id', notificationId)
        .eq('recipient_user_id', user.id)
        .eq('organization_id', currentOrganizationId);

      if (error) {
        console.error('[useUnsaveNotification] Error:', error);
        throw error;
      }
    },
    onSuccess: () => invalidateList(queryClient, currentOrganizationId, user?.id),
  });
}

export function useBatchMarkAsRead() {
  const { queryClient, user, currentOrganizationId } = useNotificationContext();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationKey: ['notifications'],
    mutationFn: async (notificationIds: string[]) => {
      if (!user?.id || !currentOrganizationId) {
        throw new Error(NOT_AUTHENTICATED_ERROR);
      }

      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .in('id', notificationIds)
        .eq('recipient_user_id', user.id)
        .eq('organization_id', currentOrganizationId);

      if (error) {
        console.error('[useBatchMarkAsRead] Error:', error);
        throw error;
      }
    },
    onSuccess: (_data, ids) => {
      invalidateAll(queryClient, currentOrganizationId, user?.id);
      toast({ title: pluralNotificationTitle(t, 'markedRead', ids.length) });
    },
    onError: (error: Error) => {
      toast({
        title: t(ERROR_TOAST_TITLE_KEY),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useBatchMarkAsUnread() {
  const { queryClient, user, currentOrganizationId } = useNotificationContext();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationKey: ['notifications'],
    mutationFn: async (notificationIds: string[]) => {
      if (!user?.id || !currentOrganizationId) {
        throw new Error(NOT_AUTHENTICATED_ERROR);
      }

      const { error } = await supabase
        .from('notifications')
        .update({ read_at: null, dismissed_at: null })
        .in('id', notificationIds)
        .eq('recipient_user_id', user.id)
        .eq('organization_id', currentOrganizationId);

      if (error) {
        console.error('[useBatchMarkAsUnread] Error:', error);
        throw error;
      }
    },
    onSuccess: (_data, ids) => {
      invalidateAll(queryClient, currentOrganizationId, user?.id);
      toast({ title: pluralNotificationTitle(t, 'markedUnread', ids.length) });
    },
    onError: (error: Error) => {
      toast({
        title: t(ERROR_TOAST_TITLE_KEY),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useBatchDismiss() {
  const { queryClient, user, currentOrganizationId } = useNotificationContext();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationKey: ['notifications'],
    mutationFn: async (notificationIds: string[]) => {
      if (!user?.id || !currentOrganizationId) {
        throw new Error(NOT_AUTHENTICATED_ERROR);
      }

      const { error } = await supabase
        .from('notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .in('id', notificationIds)
        .eq('recipient_user_id', user.id)
        .eq('organization_id', currentOrganizationId);

      if (error) {
        console.error('[useBatchDismiss] Error:', error);
        throw error;
      }
    },
    onSuccess: (_data, ids) => {
      invalidateAll(queryClient, currentOrganizationId, user?.id);
      toast({ title: pluralNotificationTitle(t, 'dismissed', ids.length) });
    },
    onError: (error: Error) => {
      toast({
        title: t(ERROR_TOAST_TITLE_KEY),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDismissAllNotifications() {
  const { queryClient, user, currentOrganizationId } = useNotificationContext();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationKey: ['notifications'],
    mutationFn: async () => {
      if (!user?.id || !currentOrganizationId) {
        throw new Error('User or organization not found');
      }

      const { error } = await supabase
        .from('notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('recipient_user_id', user.id)
        .eq('organization_id', currentOrganizationId)
        .is('dismissed_at', null);

      if (error) {
        console.error('[useDismissAllNotifications] Error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      invalidateAll(queryClient, currentOrganizationId, user?.id);
      toast({ title: t('toasts.allCleared') });
    },
    onError: (error: Error) => {
      toast({
        title: t(ERROR_TOAST_TITLE_KEY),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
